/**
 * CreditOS v2 — Mock Supabase client para modo demo/preview.
 * Ativado quando VITE_SUPABASE_URL não está definido.
 */

import { MOCK_USER, MOCK_PROFILE } from "@/lib/mock/mock-data"

// ─── Detecção de modo mock ─────────────────────────────────────────────────────

export const isMockMode = !import.meta.env.VITE_SUPABASE_URL

// ─── Mock Auth ────────────────────────────────────────────────────────────────

type AuthCallback = (event: string, session: { user: typeof MOCK_USER } | null) => void

export const mockAuth = {
  getUser: async () => ({
    data: { user: MOCK_USER },
    error: null,
  }),

  getSession: async () => ({
    data: { session: { user: MOCK_USER } },
    error: null,
  }),

  onAuthStateChange: (callback: AuthCallback) => {
    // Dispara SIGNED_IN logo após a montagem para simular sessão ativa
    const timer = setTimeout(() => {
      callback("SIGNED_IN", { user: MOCK_USER })
    }, 100)

    return {
      data: {
        subscription: {
          unsubscribe: () => clearTimeout(timer),
        },
      },
    }
  },

  signInWithPassword: async (_credentials: { email: string; password: string }) => ({
    data: { user: MOCK_USER, session: { user: MOCK_USER } },
    error: null,
  }),

  signOut: async () => ({ error: null }),

  resetPasswordForEmail: async (_email: string) => ({ error: null }),
}

// ─── Mock Storage ─────────────────────────────────────────────────────────────

export const mockStorage = {
  from: (_bucket: string) => ({
    getPublicUrl: (_path: string) => ({
      data: { publicUrl: "" },
    }),
    createSignedUrl: async (_path: string, _expires: number) => ({
      data: { signedUrl: "" },
      error: null,
    }),
  }),
}

// ─── Mock Channel ─────────────────────────────────────────────────────────────

function createMockChannel() {
  return {
    on: function () {
      return this
    },
    subscribe: function () {
      return this
    },
  }
}

// ─── Mock Query Builder ────────────────────────────────────────────────────────

/**
 * Retorna um builder fluente que no final retorna dados vazios.
 * Suficiente para que o código de produção não quebre em mock mode —
 * os hooks verificam isMockMode antes de executar queries reais.
 */
function createMockQueryBuilder(result: { data: unknown; error: null } = { data: [], error: null }) {
  const builder = {
    select: () => builder,
    eq: () => builder,
    neq: () => builder,
    gt: () => builder,
    gte: () => builder,
    lt: () => builder,
    lte: () => builder,
    ilike: () => builder,
    in: () => builder,
    order: () => builder,
    limit: () => builder,
    single: async () => ({ data: result.data as Record<string, unknown> | null, error: null }),
    then: (resolve: (v: typeof result) => void) => {
      resolve(result)
    },
  }

  // Torna o builder thenable (await funciona)
  Object.defineProperty(builder, Symbol.toStringTag, { value: "Promise" })

  return builder
}

// ─── Mock Supabase Client ─────────────────────────────────────────────────────

export const mockSupabaseClient = {
  auth: mockAuth,
  storage: mockStorage,

  from: (_table: string) => ({
    select: (_columns?: string) => createMockQueryBuilder({ data: [], error: null }),
    insert: (_data: unknown) => createMockQueryBuilder({ data: null, error: null }),
    update: (_data: unknown) => ({
      eq: () => createMockQueryBuilder({ data: null, error: null }),
    }),
    delete: () => ({
      eq: () => createMockQueryBuilder({ data: null, error: null }),
    }),
    upsert: (_data: unknown) => createMockQueryBuilder({ data: null, error: null }),
  }),

  channel: (_name: string) => createMockChannel(),

  removeChannel: async (_channel: unknown) => ({ error: null }),

  rpc: async (_fn: string, _params?: unknown) => ({ data: null, error: null }),
}

// ─── Mock Profile ─────────────────────────────────────────────────────────────

export { MOCK_USER, MOCK_PROFILE }
